import React from 'react';
import { useCart } from './CartProvider';
import { Link } from 'react-router-dom';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { state, dispatch } = useCart();

  const updateQuantity = (productId: number, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', productId, quantity });
  };

  const removeItem = (productId: number) => {
    dispatch({ type: 'REMOVE_FROM_CART', productId });
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose}></div>
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900">
            Shopping Cart 
            <span className="ml-2 bg-green-100 text-green-800 text-sm font-medium px-2.5 py-0.5 rounded-full">
              {state.itemCount} {state.itemCount === 1 ? 'item' : 'items'}
            </span>
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4" style={{
          maxHeight: 'calc(100vh - 220px)',
          scrollbarWidth: 'thin',
          scrollbarColor: '#9CA3AF #F3F4F6',
          msOverflowStyle: 'none'
        }}>
          <style>{
            `
              .cart-scroll::-webkit-scrollbar {
                width: 4px;
              }
              .cart-scroll::-webkit-scrollbar-track {
                background: #F3F4F6;
              }
              .cart-scroll::-webkit-scrollbar-thumb {
                background-color: #9CA3AF;
                border-radius: 4px;
              }
            `}
          </style>
          {state.items.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                <i className="ri-shopping-cart-line text-gray-400 text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Your cart is empty</h3>
              <p className="text-gray-600 mb-6">Add some products to get started</p>
              <Link
                to="/products"
                onClick={onClose}
                className="mt-4 w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 text-center block"
              >
                Shop Products
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {state.items.map((item) => (
                <div key={item.id} className="flex items-center space-x-4 p-4 border rounded-lg">
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{item.name}</h4>
                    <p className="text-green-600 font-bold">${item.price}</p>
                    <div className="flex items-center space-x-2 mt-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 flex items-center justify-center border rounded-full hover:bg-gray-50"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 flex items-center justify-center border rounded-full hover:bg-gray-50"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-full"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {state.items.length > 0 && (
          <div className="border-t p-6 space-y-4 bg-white flex-shrink-0">
            <div className="flex justify-between items-center text-lg font-bold">
              <span>Total: ${state.total.toFixed(2)}</span>
            </div>
            <Link 
              to="/checkout" 
              onClick={onClose}
              className="w-full bg-green-600 text-white py-3 rounded-full text-center font-semibold hover:bg-green-700 transition-colors whitespace-nowrap block"
            >
              Proceed to Checkout
            </Link>
          </div>
        )}
      </div>
    </>
  );
}